import { useNavigate } from "react-router-dom";

import { SlideClassification, Specimen, User } from "pwapi";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import useViewerItems from "hooks/useViewerItems";
import { useAppSelector } from "store/hooks";

import DiagnosisDialog from "./DiagnosisDialog";
import {
  isSpecimenDiagnosable,
  isSpecimenScreening,
} from "./DiagnosisDialogHelpers";
import { DialogEnum } from "./Dialog";
import FinalizedDiagnosisDialog from "./finalizedDiagnosisDialog/FinalizedDiagnosisDialog";
import {
  canSignoutCase,
  isFinalized,
  isScreened,
  wasSignedOutFromWPL,
} from "./utils";
import WplDiagnosisDialog from "./WplDiagnosisDialog";

export const isInputDiagnosis = (
  caseDetailObject: CaseDetailState,
  specimen?: Specimen,
  currentUser?: User,
  canSignOutInViewer?: boolean,
  screenPerSpecimen?: boolean
) => {
  const canSignOutCase = canSignoutCase(
    caseDetailObject,
    currentUser,
    canSignOutInViewer
  );

  return (
    canSignOutCase &&
    specimen &&
    isSpecimenDiagnosable(caseDetailObject, specimen, screenPerSpecimen)
  );
};

const DiagnosisDialogController = ({
  caseDetailObject,
  slideDetailObject,
  dialog,
  setDialog,
  specimen,
  aiObject,
}: {
  caseDetailObject: CaseDetailState;
  slideDetailObject: SlideDetailState;
  dialog: DialogEnum | null;
  setDialog: Function;
  specimen?: Specimen;
  aiObject?: SlideClassification;
}) => {
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth?.user);
  const canSignOutInViewer = useAppSelector((state) =>
    selectFeatureFlag(state, "signOutInViewer")
  );
  const screenPerSpecimen = useAppSelector((state) =>
    selectFeatureFlag(state, "screenPerSpecimen")
  );

  const canSignOutCase = canSignoutCase(
    caseDetailObject,
    currentUser,
    canSignOutInViewer
  );

  const viewerItems = useViewerItems();
  const nextSpecimenViewerItem = (() => {
    let foundSpecimen = false;
    for (var i = 0; i < viewerItems.length; i++) {
      const viewerItem = viewerItems[i];
      if (viewerItem.specimenId === specimen?.id) foundSpecimen = true;
      if (foundSpecimen && viewerItem.specimenId !== specimen?.id) {
        // If the case is in screening but the specimen is not up for screening, then just continue on and check the next.
        if (
          caseDetailObject.is_screening &&
          !isSpecimenScreening(caseDetailObject, viewerItem.specimenId)
        ) {
          continue;
        }
        return viewerItem;
      }
    }
    return undefined;
  })();

  const onHandleNextSpecimen = (_e: React.FormEvent) => {
    if (nextSpecimenViewerItem) {
      navigate(
        `/cases/${caseDetailObject.id}/${nextSpecimenViewerItem.type}/${nextSpecimenViewerItem.id}`
      );
    }
  };

  return isInputDiagnosis(
    caseDetailObject,
    specimen,
    currentUser,
    canSignOutInViewer,
    screenPerSpecimen
  ) && specimen ? (
    <DiagnosisDialog
      caseDetailObject={caseDetailObject}
      currentSlideId={slideDetailObject.id || 0}
      user={currentUser}
      aiObject={aiObject}
      setDialog={setDialog}
      specimen={specimen}
      onHandleNextSpecimen={onHandleNextSpecimen}
      nextSpecimenViewerItem={nextSpecimenViewerItem}
    />
  ) : // Cases which were signed out in WPL
  wasSignedOutFromWPL(caseDetailObject) ? (
    <WplDiagnosisDialog caseDetailObject={caseDetailObject} />
  ) : // Displaying a flat diagnosis, will display for both a screen and when a case is finalized
  ((isScreened(caseDetailObject.case_diagnosis) ||
      caseDetailObject.is_screening) &&
      canSignOutCase) ||
    isFinalized(caseDetailObject) ? (
    <FinalizedDiagnosisDialog
      caseDetailObject={caseDetailObject}
      slideDetailObject={slideDetailObject}
      user={currentUser}
      dialog={dialog}
      setDialog={setDialog}
      hasNextSpecimen={!!nextSpecimenViewerItem}
      onHandleNextSpecimen={onHandleNextSpecimen}
      specimen={specimen}
    />
  ) : // No diagnosis rules matched the current details of the case
  null;
};

export default DiagnosisDialogController;
